import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import UDTUserIDCard from '@/components/UDTUserIDCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, Download, TrendingUp, Clock, Shield, Activity, 
  CheckCircle, AlertTriangle, ArrowUpRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { API } from '@/config/api';

export default function DashboardHome({ user, token, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    documents: 0,
    apiDocuments: 0,
    manualDocuments: 0,
    requests: 0,
    shares: 0,
    alerts: 0,
    evidence: 0,
    activeDocuments: 0,
    expiringSoon: 0
  });
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchRecentDocs();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const docsRes = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const docs = docsRes.data.documents || [];
      const apiDocs = docs.filter(d => d.purpose?.includes('Fetched from') || d.purpose?.includes('API'));
      const activeDocs = docs.filter(d => d.status === 'active');
      
      const reqRes = await axios.get(`${API}/access/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const requests = reqRes.data.requests || [];
      
      const alertsRes = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const alerts = alertsRes.data.alerts || [];
      
      setStats({
        documents: docs.length,
        apiDocuments: apiDocs.length,
        manualDocuments: docs.length - apiDocs.length,
        requests: requests.length,
        shares: docs.filter(d => d.watermark_chain && d.watermark_chain.length > 1).length,
        alerts: alerts.length,
        evidence: alerts.filter(a => a.type === 'misuse').length,
        activeDocuments: activeDocs.length,
        expiringSoon: docs.filter(d => {
          const expiryDate = new Date(d.expiry);
          const now = new Date();
          const hoursLeft = (expiryDate - now) / (1000 * 60 * 60);
          return hoursLeft > 0 && hoursLeft < 24;
        }).length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard stats');
      // Set default stats on error
      setStats({
        documents: 0,
        apiDocuments: 0,
        manualDocuments: 0,
        requests: 0,
        shares: 0,
        alerts: 0,
        evidence: 0,
        activeDocuments: 0,
        expiringSoon: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentDocs = async () => {
    try {
      const response = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentDocs(response.data.documents.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch recent docs:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Documents',
      value: stats.documents,
      icon: FileText,
      color: '#3b82f6',
      bgColor: '#eff6ff',
      trend: '+12%',
      description: `${stats.activeDocuments} active`
    },
    {
      title: 'API Fetched',
      value: stats.apiDocuments,
      icon: Download,
      color: '#10b981',
      bgColor: '#f0fdf4',
      trend: '+8%',
      description: 'From government APIs'
    },
    {
      title: 'Pending Requests',
      value: stats.requests,
      icon: Clock,
      color: '#f59e0b',
      bgColor: '#fffbeb',
      alert: stats.requests > 0,
      description: 'Awaiting approval'
    },
    {
      title: 'Security Alerts',
      value: stats.alerts,
      icon: AlertTriangle,
      color: '#ef4444',
      bgColor: '#fef2f2',
      alert: stats.alerts > 0,
      description: `${stats.evidence} misuse detected`
    }
  ];

  return (
    <DashboardLayout user={user} token={token} onLogout={onLogout} stats={stats}>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full min-w-0">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Welcome back, {user?.name}. Here's what's happening with your documents today.
          </p>
        </div>

        {/* UDT User ID Card */}
        <div className="mb-6">
          <UDTUserIDCard user={user} />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: stat.bgColor }}
                    >
                      <Icon className="w-6 h-6" style={{ color: stat.color }} />
                    </div>
                    {stat.alert && stat.value > 0 && (
                      <Badge style={{ background: '#ef4444' }} className="animate-pulse">
                        Alert
                      </Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{stat.description}</span>
                      {stat.trend && (
                        <Badge variant="outline" style={{ color: '#10b981', borderColor: '#10b981', fontSize: '10px' }}>
                          {stat.trend}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Recent Documents */}
          <Card className="lg:col-span-2 border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Recent Documents</CardTitle>
                  <CardDescription>Your latest uploaded documents</CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/dashboard/documents')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  View All
                  <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentDocs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No documents yet</p>
                    <Button 
                      size="sm" 
                      className="mt-3"
                      style={{ background: '#3b82f6' }}
                      onClick={() => navigate('/dashboard/upload')}
                    >
                      Upload Your First Document
                    </Button>
                  </div>
                ) : (
                  recentDocs.map((doc) => (
                    <div 
                      key={doc.udt_id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate('/dashboard/documents')}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ 
                            background: doc.purpose?.includes('API') ? '#f0fdf4' : '#eff6ff' 
                          }}
                        >
                          {doc.purpose?.includes('API') ? (
                            <Download className="w-5 h-5 text-green-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            UDT ID: {doc.udt_id.substring(0, 12)}...
                          </p>
                        </div>
                      </div>
                      <Badge 
                        className="ml-3"
                        style={{ 
                          background: doc.status === 'active' ? '#10b981' : '#6b7280',
                          fontSize: '11px'
                        }}
                      >
                        {doc.status}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>Common tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start" 
                style={{ background: '#3b82f6' }}
                onClick={() => navigate('/dashboard/upload')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
              <Button 
                className="w-full justify-start" 
                style={{ background: '#10b981' }}
                onClick={() => navigate('/dashboard/fetch-docs')}
              >
                <Download className="w-4 h-4 mr-2" />
                Fetch from APIs
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/documents')}
              >
                <FileText className="w-4 h-4 mr-2" />
                View All Documents
              </Button>
              {stats.requests > 0 && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-amber-200 text-amber-700 hover:bg-amber-50"
                  onClick={() => navigate('/dashboard/requests')}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Review {stats.requests} Request{stats.requests > 1 ? 's' : ''}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Security Status */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeDocuments}</p>
                  <p className="text-sm text-gray-600">Active Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.shares}</p>
                  <p className="text-sm text-gray-600">Active Shares</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.expiringSoon}</p>
                  <p className="text-sm text-gray-600">Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
